import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows, useGLTF, Html } from '@react-three/drei';
import * as THREE from 'three';
import { clone as cloneSkinnedModel } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { MODEL_COUNT } from '../modelCatalog';

declare global {
  interface Window {
    __viewerState?: {
      loaded: boolean;
      currentModel: string;
      modelCount: number;
      canvasReady: boolean;
      animationCount: number;
      animationTime: number;
      animationNames: string[];
      activeAnimation: string | null;
      modelPosition: [number, number, number];
      activeMove: string | null;
      movementActive: boolean;
      movementProgress: number;
      modelBounds?: {
        min: [number, number, number];
        max: [number, number, number];
      };
      cameraPosition?: [number, number, number];
      cameraTarget?: [number, number, number];
      moverName: string | null;
      moverPosition: [number, number, number] | null;
    };
  }
}

export interface ScriptedMove {
  id: string;
  label: string;
  targetObjectName: string;
  animationName: string;
  restAnimationName: string;
  waypoints: [number, number, number][];
  duration: number;
}

interface ActiveMoveState {
  id: string;
  label: string;
  targetObjectName: string;
  restAnimationName: string;
  object: THREE.Object3D;
  points: THREE.Vector3[];
  segmentLengths: number[];
  totalLength: number;
  elapsed: number;
  duration: number;
}

interface ModelLoaderProps {
  modelPath: string;
  activeAnimation: string | null;
  activeScriptedMove: ScriptedMove | null;
  animationClipNames?: string[];
  fitSize: number;
  groundY: number;
  modelOffset: [number, number, number];
  modelRotation: [number, number, number];
  modelCount: number;
  onAnimationsChange: (names: string[]) => void;
  onScriptedMoveEnd: (move: ScriptedMove) => void;
}

function normalizeObjectName(name: string) {
  return name.replace(/\.\d+$/, '').replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function findObjectByName(root: THREE.Object3D, name: string) {
  const exact = root.getObjectByName(name);
  if (exact) return exact;

  const normalized = normalizeObjectName(name);
  let found: THREE.Object3D | null = null;
  root.traverse((child) => {
    if (!found && normalizeObjectName(child.name) === normalized) {
      found = child;
    }
  });
  return found;
}

function ModelLoader({
  modelPath,
  activeAnimation,
  activeScriptedMove,
  animationClipNames,
  fitSize,
  groundY,
  modelOffset,
  modelRotation,
  modelCount,
  onAnimationsChange,
  onScriptedMoveEnd,
}: ModelLoaderProps) {
  const { scene, animations } = useGLTF(modelPath);
  const model = useMemo(() => cloneSkinnedModel(scene), [scene]);
  const mixer = useMemo(() => new THREE.AnimationMixer(model), [model]);
  const moveRef = useRef<ActiveMoveState | null>(null);
  const animationClips = useMemo(() => {
    if (!animationClipNames?.length) return animations;

    const allowedNames = new Set(animationClipNames);
    return animations.filter((clip) => allowedNames.has(clip.name));
  }, [animationClipNames, animations]);
  const animationNames = useMemo(
    () => animationClips.map((clip, index) => clip.name || `Animation ${index + 1}`),
    [animationClips],
  );
  const selectedClip = useMemo(
    () => {
      const selectedIndex = activeAnimation ? animationNames.indexOf(activeAnimation) : -1;
      return animationClips[selectedIndex >= 0 ? selectedIndex : 0] ?? null;
    },
    [activeAnimation, animationClips, animationNames],
  );

  useFrame((_, delta) => {
    if (animationClips.length > 0) {
      mixer.update(delta);
      if (window.__viewerState?.currentModel === modelPath) {
        window.__viewerState.animationTime = mixer.time;
      }
    }

    const move = moveRef.current;
    if (!move) return;

    move.elapsed += delta;
    const progress = Math.min(move.elapsed / move.duration, 1);
    const distance = move.totalLength * progress;
    let covered = 0;
    let segmentIndex = 0;

    while (
      segmentIndex < move.segmentLengths.length - 1
      && covered + move.segmentLengths[segmentIndex] < distance
    ) {
      covered += move.segmentLengths[segmentIndex];
      segmentIndex += 1;
    }

    const segmentLength = Math.max(move.segmentLengths[segmentIndex], 0.0001);
    const segmentProgress = Math.min(Math.max((distance - covered) / segmentLength, 0), 1);
    const start = move.points[segmentIndex];
    const end = move.points[segmentIndex + 1];
    const position = start.clone().lerp(end, segmentProgress);
    const direction = end.clone().sub(start);

    move.object.position.copy(position);
    if (direction.lengthSq() > 0.0001) {
      move.object.rotation.y = Math.atan2(direction.x, direction.z);
    }

    if (window.__viewerState?.currentModel === modelPath) {
      window.__viewerState.activeMove = move.id;
      window.__viewerState.movementActive = true;
      window.__viewerState.movementProgress = progress;
      window.__viewerState.moverName = move.targetObjectName;
      window.__viewerState.moverPosition = [
        move.object.position.x,
        move.object.position.y,
        move.object.position.z,
      ];
    }

    if (progress >= 1) {
      moveRef.current = null;
      if (window.__viewerState?.currentModel === modelPath) {
        window.__viewerState.activeMove = null;
        window.__viewerState.movementActive = false;
        window.__viewerState.movementProgress = 1;
      }
      onScriptedMoveEnd({
        id: move.id,
        label: move.label,
        targetObjectName: move.targetObjectName,
        animationName: activeAnimation ?? '',
        restAnimationName: move.restAnimationName,
        waypoints: move.points.slice(1).map((point) => [point.x, point.y, point.z] as [number, number, number]),
        duration: move.duration,
      });
    }
  });

  useEffect(() => {
    if (model) {
      // Center the model
      // Use the loader's rest-pose bounds for fitting. SkeletonUtils.clone keeps
      // skinned meshes correctly bound, but their cloned skeleton can otherwise
      // report unstable bounds before the first animation update.
      const box = new THREE.Box3().setFromObject(scene);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z, 0.0001);
      const scale = fitSize / maxDim;

      // Center horizontally and place the bottom on the configured ground.
      const minY = box.min.y * scale;
      model.position.set(
        -center.x * scale,
        groundY - minY,
        -center.z * scale,
      );
      model.scale.setScalar(scale);
      model.rotation.set(...modelRotation);
      model.updateMatrixWorld(true);
      const fittedBox = new THREE.Box3().setFromObject(model);
      const modelBounds = {
        min: [
          fittedBox.min.x + modelOffset[0],
          fittedBox.min.y + modelOffset[1],
          fittedBox.min.z + modelOffset[2],
        ] as [number, number, number],
        max: [
          fittedBox.max.x + modelOffset[0],
          fittedBox.max.y + modelOffset[1],
          fittedBox.max.z + modelOffset[2],
        ] as [number, number, number],
      };

      // Ensure meshes have visible materials
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.frustumCulled = false;
          if (!child.material || !(child.material as THREE.Material).isMaterial) {
            child.material = new THREE.MeshStandardMaterial({ color: '#888888' });
          }
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      // Update viewer state bridge for E2E tests
      window.__viewerState = {
        loaded: true,
        currentModel: modelPath,
        modelCount,
        canvasReady: true,
        animationCount: animationClips.length,
        animationTime: 0,
        animationNames,
        activeAnimation: animationNames[0] ?? null,
        modelPosition: [
          model.position.x + modelOffset[0],
          model.position.y + modelOffset[1],
          model.position.z + modelOffset[2],
        ],
        activeMove: null,
        movementActive: false,
        movementProgress: 0,
        modelBounds,
        moverName: null,
        moverPosition: null,
      };
    }
  }, [
    animationClips.length,
    animationNames,
    fitSize,
    groundY,
    model,
    modelCount,
    modelOffset,
    modelPath,
    modelRotation,
  ]);

  useEffect(() => {
    onAnimationsChange(animationNames);
  }, [animationNames, onAnimationsChange]);

  useEffect(() => {
    mixer.stopAllAction();

    if (!selectedClip) return undefined;

    mixer.setTime(0);
    const action = mixer.clipAction(selectedClip, model);
    action.reset();
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.play();

    if (window.__viewerState?.currentModel === modelPath) {
      window.__viewerState.activeAnimation = selectedClip.name;
      window.__viewerState.animationTime = 0;
    }

    return () => {
      action.stop();
      mixer.stopAllAction();
    };
  }, [mixer, model, modelPath, selectedClip]);

  useEffect(() => {
    if (!activeScriptedMove) {
      moveRef.current = null;
      if (window.__viewerState?.currentModel === modelPath) {
        window.__viewerState.activeMove = null;
        window.__viewerState.movementActive = false;
        window.__viewerState.movementProgress = 0;
      }
      return;
    }

    if (window.__viewerState?.currentModel === modelPath) {
      window.__viewerState.activeMove = activeScriptedMove.id;
      window.__viewerState.movementActive = false;
      window.__viewerState.movementProgress = 0;
      window.__viewerState.moverName = activeScriptedMove.targetObjectName;
      window.__viewerState.moverPosition = null;
    }

    const target = findObjectByName(model, activeScriptedMove.targetObjectName);
    if (!target) {
      console.warn(`Scripted move target not found: ${activeScriptedMove.targetObjectName}`);
      return;
    }

    const points = [
      target.position.clone(),
      ...activeScriptedMove.waypoints.map((waypoint) => new THREE.Vector3(...waypoint)),
    ];
    const segmentLengths = points.slice(0, -1).map((point, index) => point.distanceTo(points[index + 1]));
    const totalLength = Math.max(segmentLengths.reduce((sum, length) => sum + length, 0), 0.0001);

    moveRef.current = {
      id: activeScriptedMove.id,
      label: activeScriptedMove.label,
      targetObjectName: activeScriptedMove.targetObjectName,
      restAnimationName: activeScriptedMove.restAnimationName,
      object: target,
      points,
      segmentLengths,
      totalLength,
      elapsed: 0,
      duration: activeScriptedMove.duration,
    };

    if (window.__viewerState?.currentModel === modelPath) {
      window.__viewerState.activeMove = activeScriptedMove.id;
      window.__viewerState.movementActive = true;
      window.__viewerState.movementProgress = 0;
      window.__viewerState.moverName = activeScriptedMove.targetObjectName;
      window.__viewerState.moverPosition = [target.position.x, target.position.y, target.position.z];
    }
  }, [activeScriptedMove, model, modelPath]);

  return (
    <group position={modelOffset}>
      <primitive object={model} />
    </group>
  );
}

function LoadingFallback() {
  return (
    <Html center>
      <div style={{
        color: '#8b8bff',
        fontSize: '16px',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        padding: '20px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(139, 139, 255, 0.3)',
          borderTop: '3px solid #8b8bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 12px',
        }} />
        Loading model...
      </div>
    </Html>
  );
}

interface ModelViewerProps {
  className?: string;
  modelPath: string;
  ambientLightIntensity?: number;
  autoRotate?: boolean;
  animationClipNames?: string[];
  background?: string;
  cameraFov?: number;
  cameraPosition?: [number, number, number];
  cameraTarget?: [number, number, number];
  fillLightIntensity?: number;
  fitSize?: number;
  frontLightIntensity?: number;
  groundY?: number;
  keyLightIntensity?: number;
  maxDistance?: number;
  minDistance?: number;
  modelOffset?: [number, number, number];
  modelRotation?: [number, number, number];
  modelCount?: number;
  scriptedMoves?: ScriptedMove[];
  showContactShadows?: boolean;
  showGrid?: boolean;
}

interface AnimationState {
  modelPath: string;
  names: string[];
  active: string | null;
}

const DEFAULT_MODEL_OFFSET: [number, number, number] = [0, 0, 0];
const DEFAULT_MODEL_ROTATION: [number, number, number] = [0, 0, 0];

function animationTestId(name: string) {
  return `animation-btn-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

export default function ModelViewer({
  modelPath,
  ambientLightIntensity = 0.4,
  autoRotate = true,
  animationClipNames,
  background = '#0f0f23',
  cameraFov = 50,
  cameraPosition = [3, 2, 5],
  cameraTarget = DEFAULT_MODEL_OFFSET,
  className,
  fillLightIntensity = 0.5,
  fitSize = 2,
  frontLightIntensity = 0,
  groundY = -1.5,
  keyLightIntensity = 1,
  maxDistance = 15,
  minDistance = 2,
  modelOffset = DEFAULT_MODEL_OFFSET,
  modelRotation = DEFAULT_MODEL_ROTATION,
  modelCount = MODEL_COUNT,
  scriptedMoves = [],
  showContactShadows = true,
  showGrid = true,
}: ModelViewerProps) {
  const [animationState, setAnimationState] = useState<AnimationState>({
    modelPath: '',
    names: [],
    active: null,
  });
  const [activeMoveId, setActiveMoveId] = useState<string | null>(null);
  const animationNames = animationState.modelPath === modelPath ? animationState.names : [];
  const activeAnimation = animationState.modelPath === modelPath ? animationState.active : null;
  const activeScriptedMove = scriptedMoves.find((move) => move.id === activeMoveId) ?? null;

  const handleAnimationsChange = useCallback((names: string[]) => {
    setAnimationState((current) => {
      const active = current.modelPath === modelPath && current.active && names.includes(current.active)
        ? current.active
        : names[0] ?? null;
      return { modelPath, names, active };
    });
  }, [modelPath]);

  const selectAnimation = (name: string) => {
    setActiveMoveId(null);
    setAnimationState((current) => ({ ...current, active: name }));
  };

  const startScriptedMove = (move: ScriptedMove) => {
    setActiveMoveId(move.id);
    setAnimationState((current) => ({ ...current, modelPath, active: move.animationName }));
  };

  const handleScriptedMoveEnd = useCallback((move: ScriptedMove) => {
    setActiveMoveId((current) => (current === move.id ? null : current));
    setAnimationState((current) => {
      if (current.modelPath !== modelPath) return current;
      return { ...current, active: move.restAnimationName };
    });
  }, [modelPath]);

  useEffect(() => {
    // Set initial state before model loads
    window.__viewerState = {
      loaded: false,
      currentModel: modelPath,
      modelCount,
      canvasReady: true,
      animationCount: 0,
      animationTime: 0,
      animationNames: [],
      activeAnimation: null,
      modelPosition: [0, 0, 0],
      activeMove: null,
      movementActive: false,
      movementProgress: 0,
      moverName: null,
      moverPosition: null,
    };
  }, [modelCount, modelPath]);

  return (
    <div className={className} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas
        camera={{ position: cameraPosition, fov: cameraFov }}
        shadows
        gl={{ antialias: true, alpha: false }}
        style={{ background }}
      >
        <ambientLight intensity={ambientLightIntensity} />
        <directionalLight
          position={[5, 5, 5]}
          intensity={keyLightIntensity}
          castShadow
          shadow-mapSize={[1024, 1024]}
        />
        <pointLight position={[-5, 3, -5]} intensity={fillLightIntensity} color="#8b8bff" />
        {frontLightIntensity > 0 && (
          <directionalLight
            position={[0, 1.6, 4.2]}
            intensity={frontLightIntensity}
            color="#fff1df"
          />
        )}

        <Suspense fallback={<LoadingFallback />}>
          <ModelLoader
            modelPath={modelPath}
            activeAnimation={activeAnimation}
            activeScriptedMove={activeScriptedMove}
            animationClipNames={animationClipNames}
            fitSize={fitSize}
            groundY={groundY}
            modelOffset={modelOffset}
            modelRotation={modelRotation}
            modelCount={modelCount}
            onAnimationsChange={handleAnimationsChange}
            onScriptedMoveEnd={handleScriptedMoveEnd}
          />
        </Suspense>

        <Suspense fallback={null}>
          <Environment preset="city" />
        </Suspense>

        {showContactShadows && (
          <ContactShadows
            position={[0, groundY, 0]}
            opacity={0.4}
            scale={10}
            blur={2.5}
            far={4}
          />
        )}

        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={minDistance}
          maxDistance={maxDistance}
          target={cameraTarget}
          autoRotate={autoRotate && animationNames.length === 0}
          autoRotateSpeed={1}
        />

        {showGrid && (
          <gridHelper args={[10, 10, '#2a2a4a', '#1a1a3a']} position={[0, groundY, 0]} />
        )}
      </Canvas>
      {animationNames.length > 0 && (
        <div className="animation-controls" data-testid="animation-controls">
          {animationNames.map((name) => (
            <button
              key={name}
              className={`animation-button ${activeAnimation === name ? 'active' : ''}`}
              data-testid={animationTestId(name)}
              onClick={() => selectAnimation(name)}
              type="button"
            >
              {name}
            </button>
          ))}
        </div>
      )}
      {scriptedMoves.length > 0 && (
        <div className="movement-controls" data-testid="movement-controls">
          {scriptedMoves.map((move) => (
            <button
              key={move.id}
              className={`movement-button ${activeMoveId === move.id ? 'active' : ''}`}
              data-testid={`movement-btn-${move.id}`}
              onClick={() => startScriptedMove(move)}
              type="button"
            >
              {move.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
